import { useState, useEffect } from 'react';
import { NAVY, GOLD, WHITE, fmt, isOver, PRIORITY_COLORS, STATUS_COLORS, STATUSES } from '../lib/ui';
import { Pill, Select, Empty, Avatar } from '../lib/ui';

export default function MyTasksView({ tasks, team, currentUserEmail, setActiveMatter, setActiveTask }) {
  const [filter,           setFilter]           = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState(() => {
    // Read which member was clicked from sessionStorage
    return sessionStorage.getItem('kl_view_member') || null;
  });

  useEffect(() => {
    // Keep in sync if sessionStorage changes
    const id = sessionStorage.getItem('kl_view_member');
    if (id) setSelectedMemberId(id);
  }, []);

  // Determine whose tasks to show
  const me = selectedMemberId
    ? team.find(m => m.id === selectedMemberId)
    : team.find(m => m.email === currentUserEmail);

  const isViewingOther = me && me.email !== currentUserEmail;

  const myTasks  = tasks.filter(t => me && (t.assignees || []).includes(me.id));
  const filtered = myTasks.filter(t => !filter || t.status === filter);

  const active    = myTasks.filter(t => t.status === 'In Progress').length;
  const overdue   = myTasks.filter(t => isOver(t.deadline) && t.status !== 'Completed' && t.status !== 'Closed').length;
  const completed = myTasks.filter(t => t.status === 'Completed' || t.status === 'Closed').length;
  const pending   = myTasks.filter(t => t.status === 'Not Started').length;

  const memberName = id => team.find(m => m.id === id)?.name || '—';

  const switchToMember = (memberId) => {
    setSelectedMemberId(memberId);
    sessionStorage.setItem('kl_view_member', memberId);
    setFilter('');
  };

  const switchToMyTasks = () => {
    setSelectedMemberId(null);
    sessionStorage.removeItem('kl_view_member');
    setFilter('');
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Team member switcher */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:12, color:'#AAA', marginRight:4 }}>Viewing:</span>
        {/* My Tasks button */}
        <button onClick={switchToMyTasks} style={{
          border:`1.5px solid ${!isViewingOther ? GOLD : '#E5E0D8'}`,
          background: !isViewingOther ? GOLD+'18' : '#FAFAF8',
          borderRadius:20, padding:'5px 14px', fontSize:12, fontWeight:600,
          color: !isViewingOther ? NAVY : '#888', cursor:'pointer', fontFamily:'inherit',
        }}>My Tasks</button>
        {/* One button per team member */}
        {team.filter(m => m.email !== currentUserEmail).map(m => (
          <button key={m.id} onClick={() => switchToMember(m.id)} style={{
            border:`1.5px solid ${selectedMemberId===m.id ? NAVY : '#E5E0D8'}`,
            background: selectedMemberId===m.id ? NAVY+'11' : '#FAFAF8',
            borderRadius:20, padding:'5px 14px', fontSize:12, fontWeight:600,
            color: selectedMemberId===m.id ? NAVY : '#888', cursor:'pointer', fontFamily:'inherit',
          }}>{m.name}</button>
        ))}
      </div>

      {/* Profile header */}
      {me && (
        <div style={{ background:WHITE, borderRadius:10, border:'1px solid #E5E0D8', borderTop:`3px solid ${GOLD}`, padding:'18px 20px', display:'flex', alignItems:'center', gap:16 }}>
          <Avatar name={me.name} size={48} />
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ fontFamily:'Georgia,serif', fontSize:18, fontWeight:700, color:NAVY }}>{me.name}</div>
              {isViewingOther && (
                <span style={{ fontSize:11, background:`${GOLD}22`, color:GOLD, border:`1px solid ${GOLD}44`, borderRadius:20, padding:'2px 10px', fontWeight:600 }}>Viewing as manager</span>
              )}
            </div>
            <div style={{ fontSize:12, color:GOLD, fontWeight:600, marginTop:2 }}>{me.role}</div>
          </div>
          <div style={{ display:'flex', gap:24 }}>
            {[
              { label:'Active',      value:active,    color:'#2980B9' },
              { label:'Overdue',     value:overdue,   color:'#C0392B' },
              { label:'Not Started', value:pending,   color:'#95A5A6' },
              { label:'Completed',   value:completed, color:'#27AE60' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:700, color, fontFamily:'Georgia,serif' }}>{value}</div>
                <div style={{ fontSize:11, color:'#AAA', marginTop:2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
        <Select value={filter} onChange={e => setFilter(e.target.value)} style={{ maxWidth:200 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </Select>
        <div style={{ fontSize:12, color:'#888' }}>
          {filtered.length} task{filtered.length !== 1 ? 's' : ''} {isViewingOther ? `assigned to ${me?.name}` : 'assigned to you'}
        </div>
      </div>

      {/* Task list */}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {!me && (
          <div style={{ background:WHITE, borderRadius:8, border:'1px solid #E5E0D8', padding:'24px', textAlign:'center', color:'#888', fontSize:13 }}>
            Your email is not linked to a team member profile. Go to Team and make sure your email is set correctly.
          </div>
        )}
        {me && filtered.length === 0 && (
          <Empty text={isViewingOther ? `No tasks assigned to ${me.name}.` : 'No tasks assigned to you.'} />
        )}
        {filtered.map(t => {
          const over = isOver(t.deadline) && t.status !== 'Completed' && t.status !== 'Closed';
          return (
            <div key={t.id}
              onClick={() => { setActiveMatter(t.matter_id); setTimeout(() => setActiveTask(t.id), 80); }}
              style={{ background:WHITE, borderRadius:8, border:`1px solid ${over?'#F5C0B8':'#E5E0D8'}`, borderLeft:`3px solid ${over?'#C0392B':PRIORITY_COLORS[t.priority]||GOLD}`, padding:'14px 16px', display:'flex', gap:14, alignItems:'center', cursor:'pointer', transition:'box-shadow 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 12px rgba(27,42,74,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4, flexWrap:'wrap' }}>
                  <span style={{ fontSize:14, fontWeight:600, color:NAVY }}>{t.title}</span>
                  <Pill label={t.priority} color={PRIORITY_COLORS[t.priority]} size={11} />
                  {over && <Pill label="Overdue" color="#C0392B" size={10} />}
                </div>
                <div style={{ fontSize:12, color:'#888' }}>
                  {t.matterName}
                  {t.deadline && <span style={{ color:over?'#C0392B':'#888', fontWeight:over?700:400 }}> · Due {fmt(t.deadline)}</span>}
                  {(t.assignees||[]).length > 1 && (
                    <span> · Also: {(t.assignees||[]).filter(id => id !== me?.id).map(memberName).join(', ')}</span>
                  )}
                </div>
              </div>
              <Pill label={t.status} color={STATUS_COLORS[t.status]} size={11} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
